import { getOrganizationTimeZone } from './org-time-zone';

/**
 * Pads a number with leading zeros to ensure it's at least 2 digits long.
 * @param num The number to pad
 * @returns A string of the padded number
 */
const pad = (num: number): string => num.toString().padStart(2, '0');

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/**
 * Parses a plain "YYYY-MM-DD" calendar date (no time/timezone component, e.g. `DatePicker`'s own
 * value shape or a `ToilDate`/`StartDate`-style field) into a local `Date` built from its date
 * parts. This is the single source of truth for that parse across the app — never
 * `new Date(isoString)` for a date-only string, since that parses as UTC midnight and can land on
 * the wrong local calendar day/weekday depending on the browser's own timezone offset.
 */
export const parseIsoDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/** Formats a `Date` back into the plain local "YYYY-MM-DD" shape `parseIsoDate` reads — the
 * counterpart used for round-tripping form values and computing `today`/date-range bounds. */
export const toIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Today as a local "YYYY-MM-DD" date — e.g. for gating a date picker's `min` to "no past dates". */
export const todayIso = (today: Date = new Date()): string => toIsoDate(today);

/** The English weekday name for a plain "YYYY-MM-DD" calendar date, read via `parseIsoDate` so it
 * reflects the local calendar day rather than a UTC-shifted one. */
export const weekdayNameOf = (dateIso: string): string | undefined => {
  const date = parseIsoDate(dateIso);
  return date ? WEEKDAY_NAMES[date.getDay()] : undefined;
};

/**
 * Converts a plain "YYYY-MM-DD" date into the full ISO-8601 UTC instant the Data Gateway's
 * `DateTime`-typed fields (e.g. `ToilRequest.ToilDate`) require: a bare date-only
 * literal fails server-side with "DateTime cannot parse the given literal of type
 * StringValueNode", both as a mutation input and as a `where` filter value, so any code sending or
 * filtering on one of these fields must go through this conversion first. Deliberately uses
 * `new Date(dateOnly).toISOString()` (date-only strings parse as UTC midnight per the ECMA-262
 * date-time string spec) rather than `parseIsoDate`'s local-midnight parse, matching how this
 * value has always been computed wherever the app already sends a `ToilDate`.
 */
export const dateOnlyToUtcIso = (dateIso: string): string => new Date(dateIso).toISOString();

const UTC_MIDNIGHT = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.0+)?Z$/;

/**
 * The "YYYY-MM-DD" a date-only `DateTime` field names (joining date, HR term bounds, ...). Those fields
 * hold the picked day at 00:00:00 UTC (`dateOnlyToUtcIso`) and are read by that date part, never
 * converted to a zone, which would show the previous day west of UTC. A value saved before that rule
 * as the organization's day start or end (e.g. `2026-10-07T18:00:00Z`, Oct 8 in Dhaka) is read by its
 * day in the organization's zone.
 */
export const dateOnlyOf = (
  value?: string | null,
  timeZone: string = getOrganizationTimeZone()
): string => {
  if (!value) return '';
  const midnight = UTC_MIDNIGHT.exec(value);
  return midnight ? midnight[1] : orgCalendarDate(value, timeZone);
};

/** A picked "YYYY-MM-DD" as the value a date-only `DateTime` field stores, or `undefined` when empty. */
export const toDateOnlyValue = (day?: string | null): string | undefined =>
  day ? dateOnlyToUtcIso(day) : undefined;

/** The day after a plain "YYYY-MM-DD" day, as "YYYY-MM-DD" (calendar arithmetic, no zone involved). */
export const nextDateOnlyDay = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date + 1)).toISOString().slice(0, 10);
};

/**
 * A `where` clause for a date-only `DateTime` field from day `from` to day `to` (both "YYYY-MM-DD",
 * inclusive, either optional): `{ $gte: from 00:00Z, $lt: day after to 00:00Z }`. `undefined` when
 * neither is set.
 */
export const dateOnlyRangeClause = (
  from?: string | null,
  to?: string | null
): { $gte?: string; $lt?: string } | undefined => {
  const range: { $gte?: string; $lt?: string } = {};
  if (from) range.$gte = `${from}T00:00:00.000Z`;
  if (to) range.$lt = `${nextDateOnlyDay(to)}T00:00:00.000Z`;
  return Object.keys(range).length > 0 ? range : undefined;
};

/** A date-only `DateTime` field (see `dateOnlyOf`) as "Mon-DD-YYYY", like the attendance day. */
export const formatDateOnly = (value?: string | null): string =>
  formatDateMonthDayYear(parseIsoDate(dateOnlyOf(value)));

// --- Calendar days in the organization's time zone ----------------------------------------------------
// A day the user picks (joining date, HR term, birthday, ...) is a day in the organization's time zone
// (see `org-time-zone.ts`). It is stored as the UTC instant of that day's start (or end) there, so in
// Bangladesh (UTC+6) 4 Oct 2026 starts at `2026-10-03T18:00:00Z`, and is read back in the same zone.

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const zonedParts = (epochMs: number, timeZone: string): ZonedParts => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(epochMs));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((entry) => entry.type === type)?.value ?? 0);
  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour') % 24,
    minute: part('minute'),
    second: part('second'),
  };
};

/** How far the zone's wall clock is ahead of UTC at that instant (DST included), in milliseconds. */
const zoneOffsetMs = (epochMs: number, timeZone: string): number => {
  const p = zonedParts(epochMs, timeZone);
  const wholeSecond = Math.floor(epochMs / 1000) * 1000;
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - wholeSecond;
};

/** The UTC instant (`YYYY-MM-DDTHH:mm:ssZ`) at which the zone's wall clock shows `day` `hh:mm:ss`. */
const zonedWallTimeToUtc = (
  day: string,
  [hour, minute, second]: [number, number, number],
  timeZone: string
): string => {
  const [year, month, date] = day.split('-').map(Number);
  const wallClock = Date.UTC(year, month - 1, date, hour, minute, second);
  // The offset is read a second time at the first guess, so a day on which DST changes still lands right.
  const firstGuess = wallClock - zoneOffsetMs(wallClock, timeZone);
  const utc = wallClock - zoneOffsetMs(firstGuess, timeZone);
  return new Date(utc).toISOString().replace('.000Z', 'Z');
};

/**
 * The organization's calendar day ("YYYY-MM-DD") of a stored `DateTime` instant — the shape date pickers
 * and `parseIsoDate` read. A value that already is a plain day is returned as it is.
 */
export const orgCalendarDate = (
  value?: string | null,
  timeZone: string = getOrganizationTimeZone()
): string => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const epochMs = new Date(value).getTime();
  if (Number.isNaN(epochMs)) return '';
  const p = zonedParts(epochMs, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
};

/** A picked day as the UTC instant its first second starts in the organization's zone (undefined when empty). */
export const orgDayStartToUtc = (
  value?: string | null,
  timeZone: string = getOrganizationTimeZone()
): string | undefined => {
  const day = orgCalendarDate(value, timeZone);
  return day ? zonedWallTimeToUtc(day, [0, 0, 0], timeZone) : undefined;
};

/** A picked day as the UTC instant of its last second in the organization's zone (undefined when empty). */
export const orgDayEndToUtc = (
  value?: string | null,
  timeZone: string = getOrganizationTimeZone()
): string | undefined => {
  const day = orgCalendarDate(value, timeZone);
  return day ? zonedWallTimeToUtc(day, [23, 59, 59], timeZone) : undefined;
};

/** The organization's wall clock at an instant: calendar parts plus weekday (0 = Sunday). */
export const orgWallClock = (
  at: Date | string,
  timeZone: string = getOrganizationTimeZone()
): ZonedParts & { weekday: number } => {
  const p = zonedParts(new Date(at).getTime(), timeZone);
  return { ...p, weekday: new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay() };
};

/** The UTC instant at which the organization's wall clock shows `day` ("YYYY-MM-DD") `hour:minute:second`. */
export const orgWallTimeToUtc = (
  day: string,
  hour: number,
  minute: number,
  second = 0,
  timeZone: string = getOrganizationTimeZone()
): string => zonedWallTimeToUtc(day, [hour, minute, second], timeZone);

/** A day-type `DateTime` field as "DD/MM/YYYY", by its day in the organization's zone. */
export const formatOrgDate = (
  value?: string | null,
  timeZone: string = getOrganizationTimeZone()
): string => CustomtDateFormat(parseIsoDate(orgCalendarDate(value, timeZone)));

/** A point in time (last login, session start, ...) as "DD/MM/YYYY, hh:mm AM" on the organization's clock. */
export const formatOrgDateTime = (
  value?: string | null,
  timeZone: string = getOrganizationTimeZone()
): string => {
  if (!value) return '';
  const epochMs = new Date(value).getTime();
  if (Number.isNaN(epochMs)) return '-';
  const p = zonedParts(epochMs, timeZone);
  return CustomtDateFormat(new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second), {
    showTime: true,
  });
};

/**
 * Formats a Date object into a string in the format "DD/MM/YYYY, HH:mm".
 * @param date The Date object to format
 * @param withoutTime Whether to exclude time from the output (default: false)
 * @returns A formatted date string
 *
 * @example
 * // Returns "15/01/2023, 09:05"
 * formatDate(new Date(2023, 0, 15, 9, 5))
 *
 * @example
 * // Returns "15/01/2023"
 * formatDate(new Date(2023, 0, 15, 9, 5), true)
 */
export const formatDate = (date: Date, withoutTime?: boolean): string => {
  const dateStr = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  const timeStr = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (withoutTime) return `${dateStr}`;
  return `${dateStr}, ${timeStr}`;
};

/**
 * Formats a date string or Date object into a customizable format
 *
 * @param dateInput - Date string or Date object to format
 * @param options - Configuration options for date formatting
 * @param options.showTime - Whether to include time in the output (default: false)
 * @param options.timeFormat - Format to use for time, 12 or 24 hour notation (default: 12)
 * @param options.dateDelimiter - Character to use between date parts (default: '/')
 * @returns Formatted date string or '-' if input is invalid
 *
 * @example
 * // Returns "20/04/2025"
 * CustomtDateFormat(new Date(2025, 3, 20))
 *
 * @example
 * // Returns "20/04/2025, 03:30 PM"
 * CustomtDateFormat(new Date(2025, 3, 20, 15, 30), { showTime: true })
 *
 * @example
 * // Returns "20-04-2025, 15:30"
 * CustomtDateFormat(new Date(2025, 3, 20, 15, 30), { showTime: true, timeFormat: 24, dateDelimiter: '-' })
 */
function CustomtDateFormat(
  dateInput: string | Date | null | undefined,
  options: {
    showTime?: boolean;
    timeFormat?: 12 | 24;
    dateDelimiter?: string;
  } = {}
): string {
  const { showTime = false, timeFormat = 12, dateDelimiter = '/' } = options;

  if (!dateInput) return '';

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const dateStr = `${day}${dateDelimiter}${month}${dateDelimiter}${year}`;

  if (!showTime) return dateStr;

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');

  if (timeFormat === 12) {
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const hoursStr = String(hours).padStart(2, '0');

    return `${dateStr}, ${hoursStr}:${minutes} ${ampm}`;
  } else {
    const hoursStr = String(hours).padStart(2, '0');
    return `${dateStr}, ${hoursStr}:${minutes}`;
  }
}

const SHORT_MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Formats a date string or Date object as "MMM-DD-YYYY", e.g. "Sep-22-2026".
 *
 * @example
 * // Returns "Sep-22-2026"
 * formatDateMonthDayYear(new Date(2026, 8, 22))
 */
export const formatDateMonthDayYear = (dateInput: string | Date | null | undefined): string => {
  if (!dateInput) return '-';
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '-';
  return `${SHORT_MONTH_NAMES[date.getMonth()]}-${pad(date.getDate())}-${date.getFullYear()}`;
};

/**
 * Formats a date string or Date object as "DD-Mon-YYYY", e.g. "22-Sep-2026".
 *
 * @param fallback Returned when the input is missing or invalid (default: '-').
 *
 * @example
 * // Returns "22-Sep-2026"
 * formatDayMonthYear(new Date(2026, 8, 22))
 */
export const formatDayMonthYear = (
  dateInput: string | Date | null | undefined,
  fallback = '-'
): string => {
  if (!dateInput) return fallback;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return fallback;
  return date
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .replace(/ /g, '-');
};
